import planck from 'planck-js';
import * as PIXI from 'pixi.js';
import GSObject from '../Base/GSObject';
import GSOUUIDArray from '../Base/GSOUUIDArray';
import Global from '../Global';
import Blok from './Blok';
import Vector from '../Base/Math/Vector';
import Player from './Player';
import { ViewDirection, levelSettings } from '../../GameKit/shared';
import Background from './Background';
import { DefaultLevelSettings } from '../Enums';
import GameKit from '../../GameKit';

function decimalColorToHTMLcolor(number) {
    let intnumber = number - 0;
    const template = '#000000';
    const red = intnumber & 0xff0000;
    const green = intnumber & 0x00ff00;
    const blue = intnumber & 0x0000ff;
    intnumber = red | green | blue;
    let HTMLcolor = intnumber.toString(16);
    HTMLcolor = template.substring(0, 7 - HTMLcolor.length) + HTMLcolor;
    return HTMLcolor;
}

export default class Level extends GSObject {
    constructor(gsoParent) {
        super(gsoParent);
        this.game = gsoParent;

        this.pixiContainer = null;
        this.pixiContainerBackgroundLayer = null;
        this.pixiContainerBackgroundOverlayLayer = null;
        this.pixiContainerBlokLayer = null;
        this.pixiContainerPlayerLayer = null;
        this.pixiContainerOverlayLayer = null;
        this.backgroundGradientSprite = null;

        this.worldBorderBody = null;

        this.title = DefaultLevelSettings.title;
        this.description = DefaultLevelSettings.description;

        this.backgroundGradientCanvas = document.createElement('canvas');
        this.backgroundGradientCanvas.width = 64;
        this.backgroundGradientCanvas.height = 64;
        this.backgroundTexture = null;
        this.backgroundColorA = DefaultLevelSettings.backgroundA;
        this.backgroundColorB = DefaultLevelSettings.backgroundB;

        this.position = new Vector(0, 0);
        this.size = new Vector(Global.NATIVE_LEVEL_WIDTH, Global.NATIVE_LEVEL_HEIGHT);

        this.background = new Background(this);

        this.blokken = new GSOUUIDArray(this, Blok);

        this.player = new Player(this);

        this.view = new Vector((Global.NATIVE_LEVEL_WIDTH * Global.NATIVE_BLOCK_SIZE) / 2, (Global.NATIVE_LEVEL_HEIGHT * Global.NATIVE_BLOCK_SIZE) / 2);

        this.viewDirection = DefaultLevelSettings.view;

        this.pointsGoal = DefaultLevelSettings.pointsGoal;
        this.timeLimit = DefaultLevelSettings.timeLimit;

        this.title = levelSettings.title;
        this.description = levelSettings.description;

        // Runtime vars
        this.onFinished = upcomingLevel => {};
        this.finished = false;
        this.upcomingLevel = '';
    }

    finish(upcomingLevel) {
        if (!this.finished) {
            const game = this.getGame();
            const foundLevel = game.levels.findByUUID(upcomingLevel);
            const next = game.levels[game.levels.indexOf(this) + 1];
            if (!foundLevel) {
                if (upcomingLevel === 'died') this.upcomingLevel = 'died';
                else if (upcomingLevel === 'time') this.upcomingLevel = 'time';
                else if (next) this.upcomingLevel = next.uuid;
                else this.upcomingLevel = null;
            } else {
                this.upcomingLevel = upcomingLevel;
            }
            this.finished = true;
        }
    }

    updateBackgroundVisuals() {
        if (!this.pixiContainerBackgroundLayer) return;

        if (this.backgroundTexture) this.backgroundTexture.destroy();

        const ctx = this.backgroundGradientCanvas.getContext('2d');
        const gradient = ctx.createLinearGradient(0, 0, 0.3, this.backgroundGradientCanvas.height);
        gradient.addColorStop(0, decimalColorToHTMLcolor(this.backgroundColorA));
        gradient.addColorStop(1, decimalColorToHTMLcolor(this.backgroundColorB));
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, this.backgroundGradientCanvas.width, this.backgroundGradientCanvas.height);

        this.backgroundTexture = PIXI.Texture.fromCanvas(this.backgroundGradientCanvas);
        if (!this.backgroundSprite) this.backgroundSprite = new PIXI.Sprite(this.backgroundTexture);
        else this.backgroundSprite.texture = this.backgroundTexture;
    }

    onCreateVisuals() {
        this.pixiContainer = new PIXI.Container();
        this.getParentPixiContainer().addChild(this.pixiContainer);

        this.pixiContainerBackgroundLayer = new PIXI.Container();
        this.updateBackgroundVisuals();
        this.backgroundSprite.x = 0;
        this.backgroundSprite.y = 0;

        this.pixiContainerBackgroundLayer.addChild(this.backgroundSprite);
        this.pixiContainer.addChild(this.pixiContainerBackgroundLayer);

        this.pixiContainerBackgroundOverlayLayer = new PIXI.Container();
        this.pixiContainer.addChild(this.pixiContainerBackgroundOverlayLayer);

        this.pixiContainerBlokLayer = new PIXI.Container();
        this.pixiContainer.addChild(this.pixiContainerBlokLayer);

        this.pixiContainerPlayerLayer = new PIXI.Container();
        this.pixiContainer.addChild(this.pixiContainerPlayerLayer);

        this.pixiContainerOverlayLayer = new PIXI.Container();
        this.pixiContainer.addChild(this.pixiContainerOverlayLayer);
    }

    onUpdateVisuals() {
        this.cullCheckObjects();

        this.pixiContainerBackgroundLayer.x = this.position.x * Global.NATIVE_BLOCK_SIZE;
        this.pixiContainerBackgroundLayer.y = this.position.y * Global.NATIVE_BLOCK_SIZE;
        this.pixiContainerBackgroundOverlayLayer.x = this.position.x * Global.NATIVE_BLOCK_SIZE;
        this.pixiContainerBackgroundOverlayLayer.y = this.position.y * Global.NATIVE_BLOCK_SIZE;

        this.backgroundSprite.scale.x = this.size.x;
        this.backgroundSprite.scale.y = this.size.y;
        this.backgroundSprite.texture.update();
    }

    onLateUpdateVisuals() {
        const game = this.getGame();

        if (this._inPlayMode) {
            const playerPosition = new Vector(this.player.pixiContainer.x, this.player.pixiContainer.y);
            /*
            if (viewToPlayerDistance > 0.05) {
                viewVelocity.normalize();
                const camSpeed = (viewToPlayerDistance / 2) * (game.deltaTime / 100);
                viewVelocity.scalem(camSpeed);
                this.view.addm(viewVelocity);
            } else {
                this.view.x = playerPosition.x;
                this.view.y = playerPosition.y;
            } */
            this.view.x = playerPosition.x;
            this.view.y = playerPosition.y;

            const viewSize = game.getViewSize();
            if (this.view.x - viewSize.x / 2 < this.position.x * Global.NATIVE_BLOCK_SIZE) this.view.x = this.position.x * Global.NATIVE_BLOCK_SIZE + viewSize.x / 2;
            if (this.view.y - viewSize.y / 2 < this.position.y * Global.NATIVE_BLOCK_SIZE) this.view.y = this.position.y * Global.NATIVE_BLOCK_SIZE + viewSize.y / 2;

            if (this.view.x + viewSize.x / 2 > (this.position.x + this.size.x) * Global.NATIVE_BLOCK_SIZE) this.view.x = (this.position.x + this.size.x) * Global.NATIVE_BLOCK_SIZE - viewSize.x / 2;
            if (this.view.y + viewSize.y / 2 > (this.position.y + this.size.y) * Global.NATIVE_BLOCK_SIZE) this.view.y = (this.position.y + this.size.y) * Global.NATIVE_BLOCK_SIZE - viewSize.y / 2;
        } else {
            const viewSize = game.getViewSize();
            const editorViewMargin = 0.3;
            const preferedView = this.view.copy();
            if (this.view.x + viewSize.x * editorViewMargin < this.position.x * Global.NATIVE_BLOCK_SIZE) preferedView.x = this.position.x * Global.NATIVE_BLOCK_SIZE - viewSize.x * editorViewMargin;
            if (this.view.y + viewSize.y * editorViewMargin < this.position.y * Global.NATIVE_BLOCK_SIZE) preferedView.y = this.position.y * Global.NATIVE_BLOCK_SIZE - viewSize.y * editorViewMargin;

            if (this.view.x - viewSize.x * editorViewMargin > (this.position.x + this.size.x) * Global.NATIVE_BLOCK_SIZE)
                preferedView.x = (this.position.x + this.size.x) * Global.NATIVE_BLOCK_SIZE + viewSize.x * editorViewMargin;
            if (this.view.y - viewSize.y * editorViewMargin > (this.position.y + this.size.y) * Global.NATIVE_BLOCK_SIZE)
                preferedView.y = (this.position.y + this.size.y) * Global.NATIVE_BLOCK_SIZE + viewSize.y * editorViewMargin;

            this.view.subm(this.view.sub(preferedView).div(new Vector(4)));
        }

        this.pixiContainer.x = Math.round(-this.view.x + game.pixiApp.renderer.width / game.pixiContainer.scale.x / 2);
        this.pixiContainer.y = Math.round(-this.view.y + game.pixiApp.renderer.height / game.pixiContainer.scale.y / 2);
    }

    onDestroyVisuals() {
        this.pixiContainer.destroy();
        this.pixiContainerBackgroundLayer.destroy();
        this.pixiContainerBackgroundLayer = null;
        this.pixiContainerBlokLayer.destroy();
        this.pixiContainerPlayerLayer.destroy();
        this.pixiContainerOverlayLayer.destroy();
        this.backgroundSprite.destroy();
        this.backgroundSprite = null;
        this.backgroundTexture.destroy();
    }

    onCreateBody(physicsWorld) {
        const self = this;
        if (this.viewDirection === ViewDirection.Side) physicsWorld.setGravity(planck.Vec2(0, 9.8));
        else physicsWorld.setGravity(planck.Vec2(0, 0));

        physicsWorld.on('begin-contact', contact => {
            const a = contact.m_fixtureA;
            const b = contact.m_fixtureB;
            if (!a.m_isSensor && !b.m_isSensor) return; // Early exit, only handling sensors here.
            let player = null;
            let other = null;
            if (a.m_body === self.player.physicsBody) {
                player = a.m_body;
                other = b.m_body;
            }
            if (b.m_body === self.player.physicsBody) {
                player = b.m_body;
                other = a.m_body;
            }
            if (player) {
                if (other.parentBlok) {
                    const actieReactie = other.parentBlok.getActiveActieReactie();
                    if (actieReactie) {
                        if (actieReactie.respondToPlayerCollision) {
                            actieReactie.react();
                        }
                    }
                }
            } else {
                if (a.m_body.parentBlok && a.m_body.m_type === 'dynamic') {
                    if (b.m_body.parentBlok && b.m_isSensor) {
                        const actieReactieB = b.m_body.parentBlok.getActiveActieReactie();
                        if (actieReactieB.respondToObjectCollision) {
                            actieReactieB.react();
                        }
                    }
                }
                if (b.m_body.parentBlok && b.m_body.m_type === 'dynamic') {
                    if (a.m_body.parentBlok && a.m_isSensor) {
                        const actieReactieA = a.m_body.parentBlok.getActiveActieReactie();
                        if (actieReactieA.respondToObjectCollision) {
                            actieReactieA.react();
                        }
                    }
                }
            }
        });

        physicsWorld.on('post-solve', (contact, impulse) => {
            const a = contact.m_fixtureA.m_body;
            const b = contact.m_fixtureB.m_body;
            let player = null;
            let other = null;
            const normal = Vector.fromPlanckVec2(contact.v_normal);
            if (a === self.player.physicsBody) {
                player = a;
                other = b;
                normal.mulm(new Vector(-1)); // Invert normal to ensure that normal is coming from bodyA
            }
            if (b === self.player.physicsBody) {
                player = b;
                other = a;
            }
            if (player) {
                if (Vector.dot(Vector.fromPlanckVec2(normal), new Vector(0, -1)) > 0.2) {
                    self.player.isGrounded = true;
                }
                if (other.parentBlok) {
                    if (other.parentBlok.isSensor) contact.setEnabled(false);
                    const actieReactie = other.parentBlok.getActiveActieReactie();
                    if (actieReactie.respondToPlayerCollision) {
                        actieReactie.react();
                    }
                }
            } else {
                if (a.parentBlok && a.m_type === 'dynamic') {
                    if (b.parentBlok) {
                        if (b.parentBlok.isSensor) {
                            contact.setEnabled(false);
                        }
                        const actieReactieB = b.parentBlok.getActiveActieReactie();
                        if (actieReactieB.respondToObjectCollision) {
                            actieReactieB.react();
                        }
                    }
                }
                if (b.parentBlok && b.m_type === 'dynamic') {
                    if (a.parentBlok) {
                        if (a.parentBlok.isSensor) {
                            contact.setEnabled(false);
                        }
                        const actieReactieA = a.parentBlok.getActiveActieReactie();
                        if (actieReactieA.respondToObjectCollision) {
                            actieReactieA.react();
                        }
                    }
                }
            }
        });

        this.worldBorderBody = physicsWorld.createBody();
        this.worldBorderBody.createFixture(
            planck.Edge(
                planck.Vec2((this.position.x * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE, (this.position.y * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE),
                planck.Vec2(((this.position.x + this.size.x) * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE, (this.position.y * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE)
            ),
            { friction: 0.2, restitution: 0 }
        );
        this.worldBorderBody.createFixture(
            planck.Edge(
                planck.Vec2((this.position.x * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE, ((this.position.y + this.size.y) * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE),
                planck.Vec2(((this.position.x + this.size.x) * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE, ((this.position.y + this.size.y) * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE)
            ),
            { friction: 0.2, restitution: 0 }
        );
        this.worldBorderBody.createFixture(
            planck.Edge(
                planck.Vec2((this.position.x * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE, (this.position.y * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE),
                planck.Vec2((this.position.x * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE, ((this.position.y + this.size.y) * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE)
            ),
            { friction: 0.2, restitution: 0 }
        );
        this.worldBorderBody.createFixture(
            planck.Edge(
                planck.Vec2(((this.position.x + this.size.x) * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE, (this.position.y * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE),
                planck.Vec2(((this.position.x + this.size.x) * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE, ((this.position.y + this.size.y) * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE)
            ),
            { friction: 0.2, restitution: 0 }
        );

        this.finished = false;
    }

    onDestroyBody(physicsWorld) {
        physicsWorld.destroyBody(this.worldBorderBody);
    }

    onUpdate(deltaTime) {
        if (!this._hasBody) {
            this.player.position.x = Math.max(this.player.position.x, this.position.x * Global.NATIVE_BLOCK_SIZE + Global.NATIVE_BLOCK_SIZE / 2);
            this.player.position.y = Math.max(this.player.position.y, this.position.y * Global.NATIVE_BLOCK_SIZE + Global.NATIVE_BLOCK_SIZE / 2);
            this.player.position.x = Math.min(this.player.position.x, (this.position.x + this.size.x) * Global.NATIVE_BLOCK_SIZE - Global.NATIVE_BLOCK_SIZE / 2);
            this.player.position.y = Math.min(this.player.position.y, (this.position.y + this.size.y) * Global.NATIVE_BLOCK_SIZE - Global.NATIVE_BLOCK_SIZE / 2);
        }
    }

    serialize() {
        const data = this.autoSerialize();
        data.backgroundColorA = this.backgroundColorA;
        data.backgroundColorB = this.backgroundColorB;
        return data;
    }

    cullCheckObjects() {
        const pos1 = this.view.copy();
        const pos2 = pos1.add(this.game.getViewSize().scale(0.5));
        pos1.subm(this.game.getViewSize().scale(0.5));
        for (let i = 0; i < this.blokken.length; i++) {
            if (this.blokken[i]) {
                const blok = this.blokken[i];

                if (gameKit != null) {
                    if (gameKit.levelEditor != null) {
                        if (gameKit.levelEditor.blokEditor != null) {
                            if (gameKit.levelEditor.blokEditor.getSelectedBlok() == blok) {
                                continue;
                            }
                        }
                    }
                }
                const blokPos = blok.getPosition();
                const buffer = (blok.size.x + blok.size.y) * blok.scale.x * Global.NATIVE_BLOCK_SIZE;
                if (blokPos.x + buffer < pos1.x || blokPos.y + buffer < pos1.y || blokPos.x - buffer > pos2.x || blokPos.y - buffer > pos2.y) {
                    blok.setCulled(true);
                } else {
                    blok.setCulled(false);
                }
            }
        }
    }
}

Level.serializeableFields = {
    uuid: String,
    position: Vector,
    size: Vector,
    view: Vector,
    blokken: [Blok],
    player: Player,
    backgroundColorA: Number,
    backgroundColorB: Number,
    background: Background,
    title: String,
    description: String,
    viewDirection: Number,
    pointsGoal: Number,
    timeLimit: Number
};
